import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  TextField,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import DialogTransition from 'src/components/DialogTransition';
import MoneyMask from 'src/components/MoneyMask/MoneyMask';
import { useDispatch, useSelector } from 'react-redux';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import { loadPendingPaymentIncomeCFDIs } from 'src/store/actions/cfdiActions';
import { LOAD_PENDING_PAYMENT_INCOME_CFDI } from 'src/store/constants/CFDI';
import {
  EDIT_AUTOMATIC_DEBIT_AMOUNT
} from 'src/store/actions/paymentsActions';
import { formatAmountWithDecimals } from 'src/utils/formatAmout';
import useStyles from './styles';
import validationSchema from './schema';

const EditAutomaticDebitAmountForm = ({
  open,
  onClose,
  automaticDebitCollectionId,
  onSubmit,
  currentAmount,
  contractId,
  currentInvoiceFolio,
  currentTransactionId
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const resetRef = useRef();
  const setFieldValueRef = useRef();
  const dispatch = useDispatch();
  const currentCfdi = useMemo(
    () => ({
      transactionId: currentTransactionId,
      folio: currentInvoiceFolio,
      series: '',
      amountIvaIncluded: currentAmount
    }),
    [currentTransactionId, currentInvoiceFolio, currentAmount]
  );

  const [cfdiList, setCfdiList] = useState([]);

  const isLoadingCfdis = useSelector(
    isLoadingSelector([LOAD_PENDING_PAYMENT_INCOME_CFDI.action])
  );
  const isSubmitting = useSelector(
    isLoadingSelector([EDIT_AUTOMATIC_DEBIT_AMOUNT.action])
  );

  const getCfdiDisplayName = cfdi => {
    if (!cfdi) return '';
    const seriesPart = cfdi.series
      ? `${t('COMMON.SERIE')} ${cfdi.series} - `
      : '';
    return `${seriesPart}${cfdi.folio} - $${formatAmountWithDecimals(
      cfdi.amountIvaIncluded,
      2
    )}`;
  };

  const loadCfdis = useCallback(() => {
    if (contractId) {
      dispatch(
        loadPendingPaymentIncomeCFDIs(contractId, loaded => {
          const alreadyIncluded = loaded.some(
            c => String(c.folio) === String(currentInvoiceFolio)
          );
          setCfdiList(alreadyIncluded ? loaded : [currentCfdi, ...loaded]);
        })
      );
    }
  }, [contractId, dispatch, currentCfdi, currentInvoiceFolio]);

  useEffect(() => {
    if (open) {
      loadCfdis();
    }
    return () => {
      if (resetRef.current) {
        resetRef.current();
      }
    };
  }, [open, loadCfdis]);

  useEffect(() => {
    if (cfdiList.length > 0 && currentInvoiceFolio && setFieldValueRef.current) {
      const match = cfdiList.find(
        c => String(c.folio) === String(currentInvoiceFolio)
      );
      if (match) {
        setFieldValueRef.current('cfdi', match);
      }
    }
  }, [cfdiList, currentInvoiceFolio]);

  if (!open) return null;

  const handleFormSubmit = values => {
    onSubmit({
      id: automaticDebitCollectionId,
      transactionId: Number(values.cfdi.transactionId),
      amount: Number(values.amount)
    });
  };

  return (
    <Dialog
      disableEscapeKeyDown
      classes={{ paper: classes.dialogRoot }}
      open={open}
      keepMounted
      TransitionComponent={DialogTransition}
    >
      <DialogTitle className={classes.header}>
        <IconButton
          key="close"
          aria-label="Close"
          color="inherit"
          onClick={onClose}
          className={classes.closeIcon}
        >
          <CloseIcon />
        </IconButton>
        <Typography color="primary" variant="h2">
          {t('ENUMS.AUTOMATIC_DEBIT.EDIT_INVOICE_AND_AMOUNT_TITLE')}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Formik
          initialValues={{ cfdi: currentCfdi, amount: currentAmount || '' }}
          validationSchema={validationSchema}
          onSubmit={handleFormSubmit}
        >
          {({
            errors,
            handleSubmit,
            touched,
            values,
            resetForm,
            setFieldValue,
            handleChange,
            handleBlur
          }) => {
            resetRef.current = () => resetForm();
            setFieldValueRef.current = setFieldValue;
            return (
              <Form onSubmit={handleSubmit}>
                <Autocomplete
                  sx={{ marginTop: 2 }}
                  fullWidth
                  options={cfdiList}
                  getOptionLabel={getCfdiDisplayName}
                  value={values.cfdi || null}
                  onChange={(_, value) => {
                    setFieldValue('cfdi', value);
                  }}
                  loading={isLoadingCfdis}
                  loadingText={t('COMMON.LOADING')}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label={
                        isLoadingCfdis ? t('COMMON.LOADING') : t('COMMON.CFDI')
                      }
                      variant="outlined"
                      fullWidth
                      placeholder={t('COMMON.SEARCH')}
                      error={Boolean(touched.cfdi && errors.cfdi)}
                      helperText={touched.cfdi && t(errors.cfdi)}
                    />
                  )}
                  noOptionsText={t('COMMON.NO_CFDIS_FOUND')}
                />

                {values.cfdi && (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ marginTop: 1 }}
                  >
                    {t('ENUMS.AUTOMATIC_DEBIT.ORIGINAL_INVOICE_AMOUNT', {
                      amount: formatAmountWithDecimals(
                        values.cfdi.amountIvaIncluded,
                        2
                      )
                    })}
                  </Typography>
                )}

                <FormControl
                  fullWidth
                  error={Boolean(touched.amount && errors.amount)}
                >
                  <TextField
                    label={t('ENUMS.AUTOMATIC_DEBIT.AMOUNT')}
                    name="amount"
                    value={values.amount}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(touched.amount && errors.amount)}
                    helperText={touched.amount && t(errors.amount)}
                    InputProps={{
                      inputComponent: MoneyMask,
                    }}
        InputLabelProps={{ shrink: true }}
                    fullWidth
                    sx={{ marginTop: 2, marginBottom: 2 }}
                  />
                </FormControl>

                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    margin: '1rem auto'
                  }}
                >
                  <Button
                    onClick={onClose}
                    color="primary"
                    variant="outlined"
                    sx={{ minWidth: '45%' }}
                  >
                    {t('COMMON.CANCEL')}
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting}
                    sx={{ minWidth: '45%' }}
                    startIcon={
                      isSubmitting ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : null
                    }
                  >
                    {t('COMMON.ACCEPT')}
                  </Button>
                </Box>
              </Form>
            );
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

EditAutomaticDebitAmountForm.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  automaticDebitCollectionId: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  contractId: PropTypes.string.isRequired,
  currentAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currentInvoiceFolio: PropTypes.string,
  currentTransactionId: PropTypes.number
};

export default EditAutomaticDebitAmountForm;
